// frontend/src/components/Footer.jsx
// Footer with version display and What's New feature
// Fetches current version dynamically from the API

import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import WhatsNewModal from "./WhatsNewModal";
import secureAxios from "../utils/secureAxios";
import { AuthContext } from "../contexts/AuthContext";

const Footer = () => {
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);

  // Fetch current version and unread count
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        // Fetch both version and unread count in parallel
        const [versionRes, unreadRes] = await Promise.all([
          secureAxios.get("/api/version"),
          secureAxios.get("/api/announcements/release-notes/unread-count")
        ]);
        
        setCurrentVersion(versionRes.data.current_version || "1.0.0");
        setUnreadCount(unreadRes.data.count || 0);
      } catch (err) {
        console.error("Error fetching footer data:", err);
        setCurrentVersion("1.0.0");
        setUnreadCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle modal close and refresh count
  const handleModalClose = () => {
    setShowWhatsNew(false);
    // Refresh the unread count after viewing
    if (user) {
      secureAxios.get("/api/announcements/release-notes/unread-count")
        .then(response => setUnreadCount(response.data.count || 0))
        .catch(() => setUnreadCount(0));
    }
  };

  return (
    <>
      <footer className="bg-dark text-white py-3 px-3 px-md-4 px-lg-5 small">
        <div className="container-fluid">
          <div className="row align-items-center text-center text-md-start">
            {/* Copyright */}
            <div className="col-12 col-md-4 mb-2 mb-md-0">
              &copy; 2025 WebVenly. All rights reserved.
            </div>
            
            {/* Center - Version */}
            <div className="col-12 col-md-4 mb-2 mb-md-0 text-center">
              <button
                className="btn btn-link btn-sm text-white text-decoration-none p-0 position-relative"
                onClick={() => setShowWhatsNew(true)}
                title="Click to see what's new"
                style={{ fontSize: "inherit" }}
              >
                <i className="bi bi-code-slash me-1"></i>
                v{currentVersion || "..."}
                
                {/* Unread badge */}
                {!loading && unreadCount > 0 && (
                  <span 
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                    <span className="visually-hidden">unread release notes</span>
                  </span>
                )}
              </button>
              
              {/* What's New link */}
              <span className="mx-2 text-muted">|</span>
              <Link 
                to="/crm/whats-new" 
                className="text-white text-decoration-none"
                style={{ fontSize: "inherit" }}
              >
                <i className="bi bi-stars me-1"></i>
                What's New
              </Link>
            </div>
            
            {/* Right side */}
            <div className="col-12 col-md-4 d-flex flex-column flex-md-row justify-content-center justify-content-md-end align-items-center gap-2">
              <span>Powered by Wyatt Multi Solutions</span>
              <Link to="/crm/tickets" className="btn btn-outline-light btn-sm">
                <i className="bi bi-chat-dots me-1"></i>
                Ticket System
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* What's New Modal - no longer needs currentVersion prop */}
      <WhatsNewModal 
        show={showWhatsNew} 
        onHide={handleModalClose}
      />
    </>
  );
};

export default Footer;